import { normalizeChartTimeframe } from "../core/chart-timeframes.js";
import { validateEffectiveCustomization } from "../customization/effective-customization.js";

export const CHART_BOOTSTRAP_SCHEMA_VERSION = 2;

const SUPPORTED_CONTEXTS = new Set(["hostedEmbed", "authenticatedWorkspace"]);

export class ChartBootstrapError extends Error {
  constructor(message) {
    super(message);
    this.name = "ChartBootstrapError";
  }
}

/**
 * Loads the authoritative chart bootstrap. Inline JSON is preferred so the
 * initial page and the client cannot observe different configuration versions.
 */
export async function loadChartBootstrap({
  document: documentObject = globalThis.document,
  endpoint = null,
  fetch: fetchImplementation = globalThis.fetch
} = {}) {
  const inline = documentObject?.querySelector?.(
    'script[type="application/json"][data-chart-bootstrap]'
  );
  if (inline) {
    return validateChartBootstrap(parseJson(inline.textContent || "", "inline"));
  }
  if (!endpoint) return null;
  if (typeof fetchImplementation !== "function") {
    throw new ChartBootstrapError("Chart bootstrap cannot be loaded in this environment.");
  }

  const response = await fetchImplementation(endpoint, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    cache: "no-store"
  });
  if (!response?.ok) {
    throw new ChartBootstrapError(
      `Chart bootstrap request failed${Number.isInteger(response?.status) ? ` (${response.status})` : ""}.`
    );
  }
  return validateChartBootstrap(await response.json());
}

export function validateChartBootstrap(value) {
  const bootstrap = record(value, "Chart bootstrap");
  if (bootstrap.schemaVersion !== CHART_BOOTSTRAP_SCHEMA_VERSION) {
    throw new ChartBootstrapError(
      `Unsupported chart bootstrap schema version ${String(bootstrap.schemaVersion)}.`
    );
  }
  if (!SUPPORTED_CONTEXTS.has(bootstrap.context)) {
    throw new ChartBootstrapError("Chart bootstrap has an unsupported launch context.");
  }

  const tenant = record(bootstrap.tenant, "Chart bootstrap tenant");
  requiredGuid(tenant.id, "tenant.id");
  requiredString(tenant.slug, "tenant.slug");
  requiredString(tenant.displayName, "tenant.displayName");

  const product = record(bootstrap.product, "Chart bootstrap product");
  requiredGuid(product.id, "product.id");
  requiredString(product.slug, "product.slug");
  requiredString(product.name, "product.name");

  const chart = record(bootstrap.chart, "Chart bootstrap chart");
  optionalGuid(chart.configurationId, "chart.configurationId");
  requiredGuid(chart.resourceId, "chart.resourceId");
  requiredString(chart.defaultSymbol, "chart.defaultSymbol");
  optionalGuid(chart.defaultSymbolId, "chart.defaultSymbolId");
  requiredString(chart.defaultTimeframe, "chart.defaultTimeframe");
  const defaultTimeframe = normalizeChartTimeframe(chart.defaultTimeframe);
  if (!defaultTimeframe) {
    throw new ChartBootstrapError("chart.defaultTimeframe is not a supported chart interval.");
  }
  if (!["dark", "light", "system"].includes(chart.defaultTheme)) {
    throw new ChartBootstrapError("chart.defaultTheme must be dark, light, or system.");
  }

  const capabilities = record(bootstrap.capabilities, "Chart bootstrap capabilities");
  requiredNonNegativeInteger(capabilities.openCharts, "capabilities.openCharts");
  requiredNonNegativeInteger(
    capabilities.indicatorsPerChart,
    "capabilities.indicatorsPerChart"
  );
  requiredStringArray(
    capabilities.availableIndicators,
    "capabilities.availableIndicators"
  );
  requiredNonNegativeInteger(capabilities.watchlistItems, "capabilities.watchlistItems");
  requiredNonNegativeInteger(capabilities.activeAlerts, "capabilities.activeAlerts");
  requiredNonNegativeInteger(capabilities.workspaces, "capabilities.workspaces");
  requiredStringArray(capabilities.drawingTools, "capabilities.drawingTools");
  if (typeof capabilities.canCreateAlerts !== "boolean") {
    throw new ChartBootstrapError("capabilities.canCreateAlerts must be a boolean.");
  }
  const endpoints = record(bootstrap.endpoints, "Chart bootstrap endpoints");
  requiredEndpoint(endpoints.workspace, "endpoints.workspace");
  requiredEndpoint(endpoints.templates, "endpoints.templates");
  requiredEndpoint(endpoints.chartResources, "endpoints.chartResources");
  requiredEndpoint(endpoints.productApi, "endpoints.productApi");
  requiredEndpoint(endpoints.alertHub, "endpoints.alertHub");

  const workspace = record(bootstrap.workspace, "Chart bootstrap workspace");
  if (typeof workspace.exists !== "boolean") {
    throw new ChartBootstrapError("workspace.exists must be a boolean.");
  }
  optionalNonNegativeInteger(workspace.schemaVersion, "workspace.schemaVersion");
  optionalNonNegativeInteger(workspace.revision, "workspace.revision");

  const customization = validateEffectiveCustomization(bootstrap.customization);

  return {
    ...bootstrap,
    chart: {
      ...chart,
      defaultTimeframe
    },
    customization
  };
}

export function productMarketDataEndpoints(productApiEndpoint) {
  requiredEndpoint(productApiEndpoint, "endpoints.productApi");
  const productApi = productApiEndpoint.replace(/\/+$/, "");
  return Object.freeze({
    history: `${productApi}/market-data/history`,
    options: `${productApi}/options`,
    realtimeGrant: `${productApi}/market-data/realtime/connection-grant`
  });
}

export function resolveChartCapabilityPolicy(capabilities, platformChartLimit = 6) {
  const source = capabilities && typeof capabilities === "object" && !Array.isArray(capabilities)
    ? capabilities
    : {};
  const configuredChartLimit = nonNegativeIntegerOrNull(source.openCharts);
  const configuredIndicatorLimit = nonNegativeIntegerOrNull(source.indicatorsPerChart);
  const configuredWatchlistItemLimit = nonNegativeIntegerOrNull(source.watchlistItems);
  const configuredActiveAlertLimit = nonNegativeIntegerOrNull(source.activeAlerts);
  const configuredWorkspaceLimit = nonNegativeIntegerOrNull(source.workspaces);
  return {
    maximumChartWindows: configuredChartLimit === null
      ? platformChartLimit
      : Math.min(platformChartLimit, configuredChartLimit),
    maximumIndicatorsPerChart: configuredIndicatorLimit ?? Number.POSITIVE_INFINITY,
    maximumWatchlistItems: configuredWatchlistItemLimit ?? Number.POSITIVE_INFINITY,
    maximumActiveAlerts: configuredActiveAlertLimit ?? Number.POSITIVE_INFINITY,
    maximumWorkspaces: configuredWorkspaceLimit ?? Number.POSITIVE_INFINITY,
    availableIndicatorIds: normalizedIdSet(source.availableIndicators),
    availableDrawingToolIds: normalizedIdSet(source.drawingTools),
    canCreateAlerts: source.canCreateAlerts !== false && configuredActiveAlertLimit !== 0
  };
}

/**
 * Returns a user-facing reason when a live indicator change adds state outside
 * the effective policy. Existing saved state may still be edited or removed.
 */
export function indicatorCreationError(
  currentInstances,
  nextInstances,
  { maximumIndicatorsPerChart = Number.POSITIVE_INFINITY, availableIndicatorIds = null } = {}
) {
  const current = Array.isArray(currentInstances) ? currentInstances : [];
  const next = Array.isArray(nextInstances) ? nextInstances : [];
  const currentById = new Map(current.map((indicator) => [
    String(indicator?.id || ""),
    String(indicator?.definitionId || "").toLowerCase()
  ]));
  const additions = next.filter((indicator) => {
    const id = String(indicator?.id || "");
    const definitionId = String(indicator?.definitionId || "").toLowerCase();
    return !currentById.has(id) || currentById.get(id) !== definitionId;
  });
  if (additions.length === 0) return null;

  const unavailable = availableIndicatorIds
    ? additions.find((indicator) => !availableIndicatorIds.has(
        String(indicator?.definitionId || "").toLowerCase()
      ))
    : null;
  if (unavailable) {
    return `${String(unavailable.definitionId || "This indicator")} is unavailable for this educator product.`;
  }

  // A workspace restored above a newly reduced limit is grandfathered by
  // instance identity. Existing instances may be edited or removed, but a new
  // instance cannot replace one while the resulting workspace remains over cap.
  if (next.length > maximumIndicatorsPerChart) {
    return Number.isFinite(maximumIndicatorsPerChart)
      ? `A chart can contain up to ${maximumIndicatorsPerChart} indicators for this educator product.`
      : "The indicator cannot be added.";
  }
  return null;
}

/**
 * Returns a user-facing reason when an action would create another drawing
 * from saved state that is no longer available to the active product.
 * Editing and deleting the existing grandfathered drawing remain permitted.
 */
export function drawingCreationError(
  drawing,
  { availableDrawingToolIds = null } = {}
) {
  const type = String(drawing?.type || "").trim().toLowerCase();
  if (availableDrawingToolIds && !availableDrawingToolIds.has(type)) {
    return `${type || "This drawing tool"} is unavailable for this educator product.`;
  }
  return null;
}

export function permittedIndicatorInstances(
  instances,
  { maximumIndicatorsPerChart = Number.POSITIVE_INFINITY, availableIndicatorIds = null } = {}
) {
  const candidates = Array.isArray(instances) ? instances : [];
  const permitted = availableIndicatorIds
    ? candidates.filter((indicator) => availableIndicatorIds.has(
        String(indicator?.definitionId || "").toLowerCase()
      ))
    : candidates;
  return permitted.slice(0, maximumIndicatorsPerChart);
}

export function createChartReadyPayload({
  activeChart = null,
  defaultSymbol,
  defaultSymbolId = null,
  defaultTimeframe,
  theme,
  rendererType = null,
  maximumChartWindows = null
}) {
  const chartAvailable = Boolean(activeChart);
  return {
    availability: chartAvailable ? "available" : "unavailable",
    ...(chartAvailable
      ? {}
      : {
          availabilityReason: maximumChartWindows === 0
            ? "open-charts-disabled"
            : "no-active-chart"
        }),
    symbol: chartAvailable ? activeChart.symbol : defaultSymbol,
    ...((chartAvailable ? activeChart.symbolId : defaultSymbolId)
      ? { symbolId: chartAvailable ? activeChart.symbolId : defaultSymbolId }
      : {}),
    timeframe: chartAvailable ? activeChart.timeframe : defaultTimeframe,
    theme,
    rendererType: chartAvailable ? rendererType || "unknown" : "none"
  };
}

export function hasPersistableChartWindows(chartInstances) {
  return Array.isArray(chartInstances)
    && chartInstances.some((instance) => instance && instance.destroyed !== true);
}

function parseJson(text, source) {
  try {
    return JSON.parse(text);
  } catch {
    throw new ChartBootstrapError(`The ${source} chart bootstrap is not valid JSON.`);
  }
}

function record(value, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ChartBootstrapError(`${label} must be an object.`);
  }
  return value;
}

function requiredString(value, field) {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new ChartBootstrapError(`${field} must be a non-empty string.`);
  }
}

function optionalString(value, field) {
  if (value !== null && value !== undefined && typeof value !== "string") {
    throw new ChartBootstrapError(`${field} must be a string or null.`);
  }
}

function requiredGuid(value, field) {
  requiredString(value, field);
  if (!isGuid(value)) throw new ChartBootstrapError(`${field} must be a GUID.`);
}

function optionalGuid(value, field) {
  if (value === null || value === undefined) return;
  if (typeof value !== "string" || !isGuid(value)) {
    throw new ChartBootstrapError(`${field} must be a GUID or null.`);
  }
}

function isGuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    .test(value);
}

function requiredEndpoint(value, field) {
  requiredString(value, field);
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    throw new ChartBootstrapError(`${field} must be an application-relative URL.`);
  }
}

function optionalNonNegativeInteger(value, field) {
  if (value === null || value === undefined) return;
  if (!Number.isInteger(value) || value < 0) {
    throw new ChartBootstrapError(`${field} must be a non-negative integer or null.`);
  }
}

function requiredNonNegativeInteger(value, field) {
  if (!Number.isInteger(value) || value < 0) {
    throw new ChartBootstrapError(`${field} must be a non-negative integer.`);
  }
}

function requiredStringArray(value, field) {
  if (!Array.isArray(value) || value.some((item) => (
    typeof item !== "string" || item.trim().length === 0
  ))) {
    throw new ChartBootstrapError(`${field} must be an array of non-empty strings.`);
  }
}

function nonNegativeIntegerOrNull(value) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : null;
}

function normalizedIdSet(value) {
  return Array.isArray(value)
    ? new Set(value.map((item) => String(item).trim().toLowerCase()).filter(Boolean))
    : null;
}
